import React, { useState, useRef, useEffect } from 'react';
import { Send, MessageCircle, RotateCcw, Heart, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useChat } from '../hooks/useChat';
import CitationList from './CitationList';

interface ChatInterfaceProps {
  userId?: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ userId = 'default' }) => {
  const [inputMessage, setInputMessage] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { chatHistory, isLoading, error, sendMessage, submitFeedback, removeFeedback, updateFeedbackDraft, clearChat, initializeChat } = useChat(userId);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  // Initialize chat on component mount
  useEffect(() => {
    if (chatHistory.length === 0) {
      initializeChat();
    }
  }, [chatHistory.length, initializeChat]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputMessage.trim() && !isLoading) {
      setInputMessage('');
      await sendMessage(inputMessage);
    }
  };

  const handleStartChat = () => {
    setIsExpanded(true);
  };

  const handleClearChat = async () => {
    await clearChat();
  };

  const handleFeedbackClick = async (item: any, value: 'positive' | 'negative') => {
    if (item.feedback?.value === value) {
      await removeFeedback(item.id);
      return;
    }

    await submitFeedback(item.id, {
      value,
      comment: item.feedback?.draftComment ?? item.feedback?.comment ?? '',
    });
  };

  const handleFeedbackNoteSave = async (item: any) => {
    if (!item.feedback?.value) {
      return;
    }

    await submitFeedback(item.id, {
      value: item.feedback.value,
      comment: item.feedback.draftComment ?? '',
    });
  };

  // Collapsed state - show MEGI introduction
  if (!isExpanded) {
    return (
      <div className="card">
        <div className="flex items-start space-x-4">
          {/* MEGI Logo */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-megi-red rounded-lg flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Megi</h3>
            <p className="text-gray-600 mb-4">
              Hi! Do you want to learn about hypertension in pregnancy?
            </p>
            <button 
              onClick={handleStartChat}
              className="text-megi-red font-semibold hover:text-megi-red-dark transition-colors"
            >
              Start chat
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expanded state - full chat interface
  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-megi-red rounded-lg flex items-center justify-center">
            <Heart className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">Megi</h3>
            <p className="text-sm text-gray-500">AI Health Assistant</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={handleClearChat}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Clear chat"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsExpanded(false)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Minimize chat"
          >
            <MessageCircle className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="h-80 overflow-y-auto mb-4 space-y-4">
        {chatHistory.map((item) => (
          <div key={item.id} className="space-y-3">
            {/* User Message */}
            {item.message && (
              <div className="flex justify-end">
                <div className="max-w-[80%] bg-megi-red text-white p-3 rounded-lg rounded-br-sm">
                  <p className="text-sm">{item.message}</p>
                </div>
              </div>
            )}
            
            {/* Assistant Response */}
            <div className="flex justify-start">
              <div className="max-w-[80%] bg-gray-100 text-gray-800 p-3 rounded-lg rounded-bl-sm">
                {item.isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-sm text-gray-500">MEGI is typing...</span>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm whitespace-pre-wrap">{item.response}</p>
                    {item.sources && item.sources.length > 0 && (
                      <CitationList sources={item.sources} />
                    )}
                    {item.responseId && (
                      <div className="mt-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleFeedbackClick(item, 'positive')}
                            disabled={item.feedback?.isSaving}
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs transition-colors ${
                              item.feedback?.value === 'positive'
                                ? 'border-green-300 bg-green-50 text-green-700'
                                : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                            } disabled:cursor-not-allowed disabled:opacity-60`}
                            aria-label="Leave positive feedback"
                          >
                            <ThumbsUp className="w-3 h-3" />
                            Helpful
                          </button>
                          <button
                            type="button"
                            onClick={() => handleFeedbackClick(item, 'negative')}
                            disabled={item.feedback?.isSaving}
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs transition-colors ${
                              item.feedback?.value === 'negative'
                                ? 'border-red-300 bg-red-50 text-red-700'
                                : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                            } disabled:cursor-not-allowed disabled:opacity-60`}
                            aria-label="Leave negative feedback"
                          >
                            <ThumbsDown className="w-3 h-3" />
                            Not helpful
                          </button>
                          {item.feedback?.isSaving && (
                            <span className="text-xs text-gray-500">Saving...</span>
                          )}
                        </div>
                        {item.feedback?.error && (
                          <p className="mt-2 text-xs text-red-600">{item.feedback.error}</p>
                        )}
                        {item.feedback?.value && (
                          <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                            <label className="mb-2 block text-xs font-medium text-gray-600">
                              Optional note
                            </label>
                            <textarea
                              value={item.feedback?.draftComment ?? ''}
                              onChange={(e) => updateFeedbackDraft(item.id, e.target.value)}
                              placeholder="Add context about what was good or what went wrong..."
                              rows={3}
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-megi-red focus:outline-none"
                              disabled={item.feedback?.isSaving}
                            />
                            <div className="mt-2 flex items-center justify-between gap-2">
                              <p className="text-xs text-gray-500">
                                Click the selected vote again to remove it.
                              </p>
                              <button
                                type="button"
                                onClick={() => handleFeedbackNoteSave(item)}
                                disabled={item.feedback?.isSaving}
                                className="rounded-full bg-megi-red px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-megi-red-dark disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                Save note
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg">
            <p className="text-sm">{error}</p>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="flex space-x-2">
        <input
          ref={inputRef}
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder="Ask me about hypertension, preeclampsia, or your BP readings..."
          className="flex-1 input-field"
          disabled={isLoading}
        />
        <button
          type="submit"
          disabled={!inputMessage.trim() || isLoading}
          className="px-4 py-3 bg-megi-red text-white rounded-lg hover:bg-megi-red-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
      
      {/* Quick questions */}
      <div className="mt-3 flex flex-wrap gap-2">
        {[
          "What is preeclampsia?",
          "How to read BP numbers?",
          "Normal BP during pregnancy?"
        ].map((question) => (
          <button
            key={question}
            onClick={() => setInputMessage(question)}
            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full transition-colors"
            disabled={isLoading}
          >
            {question}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ChatInterface;
